import React, { useState, useEffect } from 'react';
import {
    TrendingUp,
    Award,
    Target,
    User,
    CheckCircle,
    AlertCircle,
    RefreshCw,
    BookOpen,
    Brain,
    Heart,
    Dumbbell,
    BookText,
    Palette,
    Lightbulb,
    Star,
    Calendar,
    ChevronDown,
    ChevronUp,
    Activity,
    Clock,
    Trophy,
    BarChart3,
    PieChart
} from 'lucide-react';
import { API_CONFIG } from '../config/api';

const DomainIcon = ({ domain }) => {
    const iconMap = {
        "Physical Development": <Dumbbell className="w-5 h-5" />,
        "Socio-Emotional Development": <Heart className="w-5 h-5" />,
        "Cognitive Development": <Brain className="w-5 h-5" />,
        "Language & Literacy Development": <BookText className="w-5 h-5" />,
        "Aesthetic & Cultural Development": <Palette className="w-5 h-5" />,
        "Positive Learning Habits": <Lightbulb className="w-5 h-5" />
    };

    return iconMap[domain] || <BookOpen className="w-5 h-5" />;
};

const getDomainColor = (domain) => {
    const colorMap = {
        "Physical Development": "#FF7A59",
        "Socio-Emotional Development": "#F17C45",
        "Cognitive Development": "#1D3469",
        "Language & Literacy Development": "#FF7A59",
        "Aesthetic & Cultural Development": "#F17C45",
        "Positive Learning Habits": "#FF7A59"
    };

    return colorMap[domain] || "#D67C2C";
};

const StudentCompetencyMeter = ({ className = "" }) => {
    const [competencyData, setCompetencyData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);

    const server_url = API_CONFIG.BASE_URL;

    useEffect(() => {
        fetchCompetencyData();
    }, []);

    const fetchCompetencyData = async () => {
        setLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`${server_url}/student/my-competency-progress`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setCompetencyData(data);
        } catch (err) {
            setError(`Failed to load competency data: ${err.message}`);
            console.error('Error fetching student competency data:', err);
        } finally {
            setLoading(false);
        }
    };

    const getProgressColor = (percent) => {
        if (percent >= 80) return 'bg-green-500';
        if (percent >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getProgressTextColor = (percent) => {
        if (percent >= 80) return 'text-green-700';
        if (percent >= 50) return 'text-yellow-700';
        return 'text-red-700';
    };

    const getStageColor = (stage) => {
        const stageColors = {
            'Foundational': 'bg-blue-100 text-blue-800',
            'Preparatory': 'bg-green-100 text-green-800',
            'Middle': 'bg-orange-100 text-orange-800',
            'Secondary': 'bg-purple-100 text-purple-800'
        };
        return stageColors[stage] || 'bg-gray-100 text-gray-800';
    };

    const getPerformanceLevel = (percent) => {
        if (percent >= 80) return { level: 'Excellent', color: 'text-green-600', icon: <Trophy className="w-4 h-4" /> };
        if (percent >= 60) return { level: 'Good', color: 'text-blue-600', icon: <Star className="w-4 h-4" /> };
        if (percent >= 40) return { level: 'Developing', color: 'text-yellow-600', icon: <TrendingUp className="w-4 h-4" /> };
        return { level: 'Needs Support', color: 'text-red-600', icon: <AlertCircle className="w-4 h-4" /> };
    };

    if (loading) {
        return (
            <div className={`bg-white rounded-2xl border border-gray-200 p-8 ${className}`}>
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D67C2C]"></div>
                    <span className="ml-4 text-gray-600 font-[poppins] text-lg">Loading your competency progress...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`bg-white rounded-2xl border border-red-200 p-8 ${className}`}>
                <div className="flex items-center gap-3 text-red-700 mb-6">
                    <AlertCircle className="w-6 h-6" />
                    <span className="font-semibold font-[poppins] text-lg">Error Loading Competency Data</span>
                </div>
                <p className="text-red-600 mb-6 font-[poppins]">{error}</p>
                <button
                    onClick={fetchCompetencyData}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-[poppins] font-semibold"
                >
                    <RefreshCw className="w-5 h-5" />
                    Retry Loading
                </button>
            </div>
        );
    }

    if (!competencyData) {
        return (
            <div className={`bg-white rounded-2xl border border-gray-200 p-8 ${className}`}>
                <div className="text-center text-gray-500 font-[poppins] text-lg">
                    No competency data available
                </div>
            </div>
        );
    }

    const performanceLevel = getPerformanceLevel(competencyData.progress_percentage);

    return (
        <div className={`bg-white rounded-2xl border border-gray-200 ${className}`}>
            {/* Header */}
            <div className="p-8 border-b border-gray-200">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 font-[poppins] mb-2">
                            My Competency Progress
                        </h2>
                        <p className="text-gray-600 font-[poppins]">
                            Track your learning journey across all domains
                        </p>
                    </div>
                    <div className="text-right">
                        <div className={`text-4xl font-bold font-[poppins] ${getProgressTextColor(competencyData.progress_percentage)}`}>
                            {Math.round(competencyData.progress_percentage)}%
                        </div>
                        <div className="text-gray-600 font-[poppins]">Overall Progress</div>
                    </div>
                </div>

                {/* Overall Progress Card */}
                <div className="bg-gradient-to-r from-[#D67C2C] to-[#FF7A59] rounded-2xl p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-xl font-bold font-[poppins] mb-1">
                                {competencyData.current_stage} Stage
                            </h3>
                            <p className="text-white/90 font-[poppins]">
                                Grade {competencyData.grade}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {performanceLevel.icon}
                            <span className="font-semibold font-[poppins]">{performanceLevel.level}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white/20 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-semibold font-[poppins]">Completed</span>
                            </div>
                            <div className="text-2xl font-bold font-[poppins]">
                                {competencyData.completed_competencies}
                            </div>
                            <div className="text-white/80 text-sm font-[poppins]">competencies</div>
                        </div>
                        <div className="bg-white/20 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Target className="w-5 h-5" />
                                <span className="font-semibold font-[poppins]">Total</span>
                            </div>
                            <div className="text-2xl font-bold font-[poppins]">
                                {competencyData.total_competencies}
                            </div>
                            <div className="text-white/80 text-sm font-[poppins]">competencies</div>
                        </div>
                        <div className="bg-white/20 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-5 h-5" />
                                <span className="font-semibold font-[poppins]">Remaining</span>
                            </div>
                            <div className="text-2xl font-bold font-[poppins]">
                                {competencyData.total_competencies - competencyData.completed_competencies}
                            </div>
                            <div className="text-white/80 text-sm font-[poppins]">to complete</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Domain Progress */}
            <div className="p-8">
                <div className="flex items-center gap-2 mb-6">
                    <BarChart3 className="w-5 h-5 text-[#D67C2C]" />
                    <h3 className="text-xl font-bold text-gray-900 font-[poppins]">Domain Progress</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {Object.entries(competencyData.competency_breakdown).map(([domain, breakdown]) => (
                        <div
                            key={domain}
                            className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 hover:border-[#D67C2C]"
                            onClick={() => setSelectedDomain(selectedDomain === domain ? null : domain)}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                                        style={{ backgroundColor: getDomainColor(domain) }}
                                    >
                                        <DomainIcon domain={domain} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 font-[poppins]">
                                            {domain}
                                        </h4>
                                        <p className="text-sm text-gray-600 font-[poppins]">
                                            {breakdown.completed}/{breakdown.total} completed
                                        </p>
                                    </div>
                                </div>
                                {selectedDomain === domain
                                    ? <ChevronUp className="w-5 h-5 text-[#D67C2C]" />
                                    : <ChevronDown className="w-5 h-5 text-gray-400" />
                                }
                            </div>

                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-600 font-[poppins]">Progress</span>
                                    <span className={`text-lg font-bold font-[poppins] ${getProgressTextColor(breakdown.percentage)}`}>
                                        {Math.round(breakdown.percentage)}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(breakdown.percentage)}`}
                                        style={{ width: `${Math.min(breakdown.percentage, 100)}%` }}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {breakdown.percentage >= 80 && (
                                        <div className="flex items-center gap-1 text-green-600">
                                            <CheckCircle className="w-4 h-4" />
                                            <span className="text-sm font-[poppins]">Mastered</span>
                                        </div>
                                    )}
                                    {breakdown.percentage >= 50 && breakdown.percentage < 80 && (
                                        <div className="flex items-center gap-1 text-yellow-600">
                                            <TrendingUp className="w-4 h-4" />
                                            <span className="text-sm font-[poppins]">Developing</span>
                                        </div>
                                    )}
                                    {breakdown.percentage < 50 && (
                                        <div className="flex items-center gap-1 text-red-600">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="text-sm font-[poppins]">Needs Focus</span>
                                        </div>
                                    )}
                                </div>
                                <span className="text-xs text-gray-500 font-[poppins]">
                                    {competencyData.current_stage} Stage
                                </span>
                            </div>

                            {/* Expanded Domain Details */}
                            {selectedDomain === domain && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="space-y-4">
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-700 font-[poppins] mb-2">
                                                Competency Details
                                            </h5>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div className="bg-white rounded-lg p-3">
                                                    <div className="text-green-600 font-bold text-lg">
                                                        {breakdown.completed}
                                                    </div>
                                                    <div className="text-gray-600 font-[poppins]">Completed</div>
                                                </div>
                                                <div className="bg-white rounded-lg p-3">
                                                    <div className="text-orange-600 font-bold text-lg">
                                                        {breakdown.total - breakdown.completed}
                                                    </div>
                                                    <div className="text-gray-600 font-[poppins]">Remaining</div>
                                                </div>
                                            </div>
                                        </div>

                                        {breakdown.percentage < 100 && (
                                            <div className="bg-blue-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Target className="w-4 h-4 text-blue-600" />
                                                    <span className="text-sm font-semibold text-blue-700 font-[poppins]">
                                                        Next Steps
                                                    </span>
                                                </div>
                                                <p className="text-sm text-blue-600 font-[poppins]">
                                                    {breakdown.percentage < 50
                                                        ? "Focus on foundational skills in this domain through assignments and activities."
                                                        : breakdown.percentage < 80
                                                            ? "Continue practicing to strengthen your understanding in this area."
                                                            : "You're doing great! Complete the remaining competencies to master this domain."
                                                    }
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Learning Insights */}
                {competencyData.multidisciplinary_progress && Object.keys(competencyData.multidisciplinary_progress).length > 0 && (
                    <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Star className="w-5 h-5 text-purple-600" />
                            <h4 className="text-lg font-semibold text-purple-900 font-[poppins]">
                                Learning Insights
                            </h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h5 className="text-sm font-semibold text-purple-700 font-[poppins] mb-2">
                                    Your Strengths
                                </h5>
                                <div className="space-y-2">
                                    {Object.entries(competencyData.competency_breakdown)
                                        .filter(([_, breakdown]) => breakdown.percentage >= 70)
                                        .slice(0, 3)
                                        .map(([domain, breakdown]) => (
                                            <div key={domain} className="flex items-center gap-2 text-sm text-purple-700 font-[poppins]">
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                                <span>{domain}</span>
                                                <span className="text-green-600 font-semibold">
                                                    {Math.round(breakdown.percentage)}%
                                                </span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>

                            <div>
                                <h5 className="text-sm font-semibold text-purple-700 font-[poppins] mb-2">
                                    Areas to Focus On
                                </h5>
                                <div className="space-y-2">
                                    {Object.entries(competencyData.competency_breakdown)
                                        .filter(([_, breakdown]) => breakdown.percentage < 70)
                                        .slice(0, 3)
                                        .map(([domain, breakdown]) => (
                                            <div key={domain} className="flex items-center gap-2 text-sm text-purple-700 font-[poppins]">
                                                <TrendingUp className="w-4 h-4 text-orange-500" />
                                                <span>{domain}</span>
                                                <span className="text-orange-600 font-semibold">
                                                    {Math.round(breakdown.percentage)}%
                                                </span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Motivational Message */}
                <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Trophy className="w-6 h-6 text-green-600" />
                        <h4 className="text-lg font-semibold text-green-900 font-[poppins]">
                            Keep Up the Great Work!
                        </h4>
                    </div>
                    <p className="text-green-700 font-[poppins]">
                        {competencyData.progress_percentage >= 80
                            ? "You're excelling in your learning journey! Continue to challenge yourself with new activities."
                            : competencyData.progress_percentage >= 50
                                ? "You're making good progress! Keep practicing and you'll master more competencies soon."
                                : "Every expert was once a beginner. Keep learning and practicing - you're on the right path!"
                        }
                    </p>
                    <div className="mt-4">
                        <div className="text-2xl font-bold text-green-800 font-[poppins]">
                            {competencyData.completed_competencies} / {competencyData.total_competencies}
                        </div>
                        <div className="text-green-600 font-[poppins]">Competencies Mastered</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentCompetencyMeter;